import { useRef, useState } from "react";

import {
  MdAdd,
  MdSend,
  MdImage,
  MdRestaurantMenu,
  MdClose,
  MdMenuBook,
  MdShoppingCart,
} from "react-icons/md";

import "./MessageInput.css";

function MessageInput({
  mode = "customer",
  conversation,
  onSendMessage,
  onOpenMenu,
  onOpenMyMenu,
  onOpenOrder,
  disabled = false,
}) {

  // =========================================================
  // STATE
  // =========================================================

  const [text, setText] = useState("");

  const [
    showActions,
    setShowActions,
  ] = useState(false);

  const [
    sending,
    setSending,
  ] = useState(false);

  const textareaRef = useRef(null);


  // =========================================================
  // PROVIDER INFORMATION
  // =========================================================

  const provider =
    conversation?.providerId ||
    conversation?.provider ||
    null;


  const providerType =
    provider?.providerType ||
    "Food Provider";


  // =========================================================
  // NORMALIZED PROVIDER TYPE
  // =========================================================

  const normalizedProviderType =
    String(providerType)
      .toLowerCase()
      .trim();


  // =========================================================
  // CUSTOM ORDER AVAILABILITY
  // =========================================================

  const customOrderAllowed = [
    "home chef",
    "bakery",
    "catering",
    "provision",
    "provision store",
  ].includes(normalizedProviderType);


  // =========================================================
  // SEND NORMAL MESSAGE
  // =========================================================

  const handleSubmit = async (event) => {

    event?.preventDefault();

    const trimmedText =
      text.trim();


    if (
      !trimmedText ||
      disabled ||
      sending
    ) {
      return;
    }


    try {

      setSending(true);


      await onSendMessage({

        message:
          trimmedText,

        messageType:
          "text",

      });


      setText("");

      setShowActions(false);


      // Reset textarea height

      if (textareaRef.current) {

        textareaRef.current.style.height =
          "auto";

      }

    } catch (error) {

      console.error(
        "Send Message Error:",
        error
      );

    } finally {

      setSending(false);

    }

  };


  // =========================================================
  // ENTER KEY
  // =========================================================

  const handleKeyDown = (event) => {

    if (
      event.key === "Enter" &&
      !event.shiftKey
    ) {

      event.preventDefault();

      handleSubmit(event);

    }

  };


  // =========================================================
  // TEXTAREA AUTO RESIZE
  // =========================================================

  const handleTextChange = (event) => {

    const value =
      event.target.value;


    setText(value);


    const textarea =
      event.target;


    textarea.style.height =
      "auto";


    textarea.style.height =
      `${Math.min(
        textarea.scrollHeight,
        120
      )}px`;

  };


  // =========================================================
  // OPEN CUSTOMER MENU
  // =========================================================

  const handleOpenMenu = () => {

    if (disabled) {
      return;
    }


    setShowActions(false);


    if (onOpenMenu) {

      onOpenMenu({

        provider,
        providerType,
        conversation,

      });

    }

  };


  // =========================================================
  // OPEN CUSTOMER CUSTOM ORDER
  // =========================================================

  const handleOpenOrder = () => {

    if (
      disabled ||
      !customOrderAllowed
    ) {
      return;
    }


    setShowActions(false);


    if (onOpenOrder) {

      onOpenOrder({

        provider,
        providerType,
        conversation,

      });

    }

  };


  // =========================================================
  // OPEN PROVIDER MY MENU
  // =========================================================

  const handleOpenMyMenu = () => {

    if (disabled) {
      return;
    }


    setShowActions(false);


    if (onOpenMyMenu) {

      onOpenMyMenu({

        provider,
        providerType,
        conversation,

      });

    }

  };


  // =========================================================
  // TOGGLE QUICK ACTIONS
  // =========================================================

  const toggleActions = () => {

    if (disabled) {
      return;
    }


    setShowActions(
      previous =>
        !previous
    );

  };


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="message-input-wrapper">


      {/* =====================================================
          QUICK ACTIONS
      ===================================================== */}

      {showActions && (

        <div className="message-quick-actions">


          {/* =================================================
              TITLE
          ================================================= */}

          <div className="quick-actions-title">

            Quick Actions

          </div>


          {/* =================================================
              PROVIDER ACTIONS
          ================================================= */}

          {mode === "provider" && (

            <>

              {/* =============================================
                  ADD MENU
              ============================================= */}

              <button
                type="button"
                onClick={handleOpenMenu}
                disabled={disabled}
              >

                <span className="quick-action-icon">

                  <MdRestaurantMenu />

                </span>


                <span className="quick-action-content">

                  <strong>
                    Add Menu
                  </strong>

                  <small>
                    Add an item to your menu
                  </small>

                </span>

              </button>


              {/* =============================================
                  MY MENU
              ============================================= */}

              <button
                type="button"
                onClick={handleOpenMyMenu}
                disabled={disabled}
              >

                <span className="quick-action-icon">

                  <MdMenuBook />

                </span>


                <span className="quick-action-content">

                  <strong>
                    My Menu
                  </strong>

                  <small>
                    View and manage your menu
                  </small>

                </span>

              </button>

            </>

          )}


          {/* =================================================
              CUSTOMER ACTIONS
          ================================================= */}

          {mode === "customer" && (

            <>

              {/* =============================================
                  MENU
              ============================================= */}

              <button
                type="button"
                onClick={handleOpenMenu}
                disabled={disabled}
              >

                <span className="quick-action-icon">

                  <MdRestaurantMenu />

                </span>


                <span className="quick-action-content">

                  <strong>
                    Menu
                  </strong>

                  <small>
                    View {providerType} menu
                  </small>

                </span>

              </button>


              {/* =============================================
                  CUSTOM ORDER
              ============================================= */}

              {customOrderAllowed ? (

                <button
                  type="button"
                  onClick={handleOpenOrder}
                  disabled={disabled}
                >

                  <span className="quick-action-icon">

                    <MdShoppingCart />

                  </span>


                  <span className="quick-action-content">

                    <strong>
                      Order
                    </strong>

                    <small>
                      Create a custom order
                    </small>

                  </span>

                </button>

              ) : (

                /* =========================================
                   RESTAURANT / HOTEL COMING SOON
                ========================================= */

                <button
                  type="button"
                  className="quick-action-coming-soon"
                  disabled
                >

                  <span className="quick-action-icon">

                    <MdShoppingCart />

                  </span>


                  <span className="quick-action-content">

                    <strong>
                      Order
                    </strong>

                    <small>
                      Coming soon for {providerType}
                    </small>

                  </span>

                </button>

              )}

            </>

          )}

        </div>

      )}


      {/* =====================================================
          INPUT FORM
      ===================================================== */}

      <form
        className="message-input-form"
        onSubmit={handleSubmit}
      >


        {/* ===================================================
            ADD / QUICK ACTIONS BUTTON
        =================================================== */}

        <button
          type="button"
          className={`message-add-button ${
            showActions
              ? "active"
              : ""
          }`}
          onClick={toggleActions}
          disabled={disabled}
          aria-label="Quick actions"
          title="Quick actions"
        >

          {showActions ? (

            <MdClose />

          ) : (

            <MdAdd />

          )}

        </button>


        {/* ===================================================
            IMAGE BUTTON
        =================================================== */}

        <button
          type="button"
          className="message-image-button"
          disabled={true}
          aria-label="Send image"
          title="Image messaging coming soon"
        >

          <MdImage />

        </button>


        {/* ===================================================
            TEXTAREA
        =================================================== */}

        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={
            disabled
              ? "Conversation unavailable"
              : "Type a message..."
          }
          disabled={
            disabled ||
            sending
          }
          rows={1}
        />


        {/* ===================================================
            SEND BUTTON
        =================================================== */}

        <button
          type="submit"
          className={`message-send-button ${
            sending
              ? "sending"
              : ""
          }`}
          disabled={
            disabled ||
            sending ||
            !text.trim()
          }
          aria-label="Send message"
          title="Send message"
        >

          <MdSend />

        </button>

      </form>


      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div className="message-input-footer">

        <span>
          🔒 Messages are private and secure
        </span>


        <span className="message-input-hint">
          Press Enter to send
        </span>

      </div>

    </div>

  );

}

export default MessageInput;